/**
 * Halloween Nose Picker
 *
 * The category tiles are rendered server-side in Liquid (image + title), so the
 * merchant uploads a category image in the theme editor. This file only drives
 * the cascade that opens underneath a tile:
 *
 *   Colour -> Finish -> Nose model -> Add to cart
 *
 * Every colour, label, swatch image, price and stock state is read from the
 * live product catalogue. No variant data is hardcoded here.
 *
 * Products with a [Shape x Colour] option set (the glow and star series) have
 * no Finish option, so the finish step removes itself for those.
 */

const HNP_FINISH_ORDER = ['matte', 'gloss', 'glitter'];

const hnpHandleize = (s) =>
  String(s)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

class HalloweenNosePicker extends HTMLElement {
  connectedCallback() {
    if (this.hnpReady) return;

    const variantNode = this.querySelector('[data-hnp-variants]');
    const categoryNode = this.querySelector('[data-hnp-categories]');

    // connectedCallback can fire before our children are parsed. Wait for them
    // rather than reading null. Guarded by an attempt count so a genuinely
    // missing script tag fails loudly instead of spinning forever.
    if (!variantNode || !categoryNode) {
      this.hnpTries = (this.hnpTries || 0) + 1;

      if (this.hnpTries > 60) {
        console.error(
          '[halloween-nose-picker] section data missing. Expected <script data-hnp-variants> and <script data-hnp-categories> inside the element.'
        );
        return;
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
      } else {
        requestAnimationFrame(() => this.connectedCallback());
      }
      return;
    }

    try {
      this.entries = JSON.parse(variantNode.textContent);
      this.categories = JSON.parse(categoryNode.textContent);
    } catch (err) {
      console.error('[halloween-nose-picker] could not parse section data', err);
      return;
    }

    this.hnpReady = true;

    this.allowOutOfStock = this.dataset.allowOos === 'true';
    this.sel = { cat: null, color: null, finish: null, model: null };

    this.categories.forEach((cat) => {
      cat.rules = this.parseRules(cat.colorRules);
    });

    this.panel = this.querySelector('[data-hnp-panel]');
    this.status = this.querySelector('[data-hnp-status]');

    this.paintCounts();
    this.addEventListener('click', this.onClick.bind(this));
  }

  /* ------------------------------------------------------- colour rules */

  /**
   * Rule syntax, comma separated:
   *   Obsidian           exact colour name
   *   ~glow              every colour whose name contains "glow"
   *   -Custom Color      exclude
   *   Green=Green Star   exact match, relabelled for display
   * An empty setting means "every colour these products actually have".
   */
  parseRules(raw) {
    const include = [];
    const exclude = [];

    String(raw || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((token) => {
        if (token.startsWith('-')) {
          exclude.push(hnpHandleize(token.slice(1)));
          return;
        }
        const parts = token.split('=');
        const left = (parts[0] || '').trim();
        const right = (parts[1] || '').trim();
        if (left.startsWith('~')) {
          include.push({ type: 'contains', value: hnpHandleize(left.slice(1)), label: null });
        } else {
          include.push({ type: 'exact', value: hnpHandleize(left), label: right || null });
        }
      });

    return { include, exclude, openEnded: include.length === 0 };
  }

  /** Colours the catalogue actually has for this category, in the merchant's order. */
  colorsFor(cat) {
    const live = this.entries.filter((e) => {
      if (!this.allowOutOfStock && !e.a) return false;
      if (cat.products.length && cat.products.indexOf(e.ph) === -1) return false;
      return true;
    });

    const discovered = new Map();
    live.forEach((e) => {
      if (!discovered.has(e.c)) {
        discovered.set(e.c, { h: e.c, label: e.cn, img: e.cimg || '', matches: [] });
      }
      const rec = discovered.get(e.c);
      rec.matches.push(e);
      if (!rec.img && e.cimg) rec.img = e.cimg;
    });

    const { include, exclude, openEnded } = cat.rules;
    const ordered = [];
    const seen = new Set();

    const push = (rec, label) => {
      if (!rec || seen.has(rec.h) || exclude.indexOf(rec.h) !== -1) return;
      seen.add(rec.h);
      ordered.push(Object.assign({}, rec, { label: label || rec.label }));
    };

    if (openEnded) {
      [...discovered.values()].sort((a, b) => a.label.localeCompare(b.label)).forEach((r) => push(r));
    } else {
      include.forEach((rule) => {
        if (rule.type === 'exact') {
          push(discovered.get(rule.value), rule.label);
        } else {
          [...discovered.values()]
            .filter((r) => r.h.indexOf(rule.value) !== -1)
            .sort((a, b) => a.label.localeCompare(b.label))
            .forEach((r) => push(r));
        }
      });
    }

    return ordered.map((r) =>
      Object.assign({}, r, {
        count: r.matches.length,
        from: Math.min(...r.matches.map((e) => e.p)),
      })
    );
  }

  /* ---------------------------------------------------------------- pool */

  pool(filter = {}) {
    const cat = filter.cat || this.sel.cat;
    if (!cat) return [];
    const allowed = new Set(this.colorsFor(cat).map((c) => c.h));

    return this.entries.filter((e) => {
      if (!allowed.has(e.c)) return false;
      if (!this.allowOutOfStock && !e.a) return false;
      if (cat.products.length && cat.products.indexOf(e.ph) === -1) return false;
      if (filter.color && e.c !== filter.color) return false;
      if (filter.finish !== undefined && filter.finish !== null && e.f !== filter.finish) return false;
      if (filter.model && e.m !== filter.model) return false;
      return true;
    });
  }

  finishesFor(color) {
    const seen = new Map();
    this.pool({ color }).forEach((e) => {
      if (!seen.has(e.f)) seen.set(e.f, { key: e.f, label: e.fn, count: 0 });
      seen.get(e.f).count += 1;
    });

    const list = [...seen.values()].sort(
      (a, b) => HNP_FINISH_ORDER.indexOf(a.key) - HNP_FINISH_ORDER.indexOf(b.key)
    );

    // A colour can exist on both a [Finish x Colour] product and a
    // [Shape x Colour] one, which would render an unlabelled chip. Give the
    // finish-less variants a name rather than a blank button. When '' is the
    // only entry it stays blank, which is the signal to skip the step.
    if (list.length > 1) {
      list.forEach((f) => {
        if (f.key === '') f.label = 'Standard';
      });
    }

    return list;
  }

  modelsFor(color, finish) {
    const seen = new Map();
    this.pool({ color, finish }).forEach((e) => {
      if (!seen.has(e.m)) seen.set(e.m, { key: e.m, label: e.mn, img: e.img });
    });
    return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  currentVariant() {
    const { color, finish, model } = this.sel;
    if (!color || finish === null || !model) return null;
    return this.pool({ color, finish, model })[0] || null;
  }

  /* -------------------------------------------------------------- events */

  paintCounts() {
    this.categories.forEach((cat) => {
      const tile = this.querySelector(`[data-hnp-tile="${cat.key}"]`);
      if (!tile) return;
      const colors = this.colorsFor(cat);
      const slot = tile.querySelector('[data-hnp-count]');
      if (slot) {
        slot.textContent = colors.length
          ? `${colors.length} colour${colors.length === 1 ? '' : 's'}`
          : 'Sold out';
      }
      if (!colors.length) tile.setAttribute('disabled', '');
    });
  }

  onClick(event) {
    const tile = event.target.closest('[data-hnp-tile]');
    if (tile) return this.pickCategory(tile.dataset.hnpTile);

    const chip = event.target.closest('[data-hnp-chip]');
    if (chip) return this.pickChip(chip.dataset.step, chip.dataset.value);

    if (event.target.closest('[data-hnp-reset]')) return this.reset();

    const submit = event.target.closest('[data-hnp-submit]');
    if (submit) return this.addToCart(submit);
  }

  pickCategory(key) {
    const cat = this.categories.find((c) => c.key === key);
    if (!cat) return;

    // Clicking the tile that is already open closes it.
    if (this.sel.cat && this.sel.cat.key === key) return this.reset();

    this.sel = { cat, color: null, finish: null, model: null };
    this.syncTiles();
    this.renderPanel();
    this.panel.hidden = false;
    this.panel.scrollIntoView({
      behavior: this.motionOk() ? 'smooth' : 'auto',
      block: 'nearest',
    });
    this.announce(`${cat.title} open. Choose a colour.`);
  }

  pickChip(step, value) {
    if (step === 'color') {
      this.sel.color = value;
      this.sel.finish = null;
      this.sel.model = null;

      const finishes = this.finishesFor(value);
      // Series with no Finish option: skip that step.
      if (finishes.length === 1 && finishes[0].key === '') this.sel.finish = '';
    } else if (step === 'finish') {
      this.sel.finish = value;
      this.sel.model = null;
    } else if (step === 'model') {
      this.sel.model = value;
    }

    // If only one model is possible, select it instead of showing a step of one.
    if (this.sel.color && this.sel.finish !== null && !this.sel.model) {
      const models = this.modelsFor(this.sel.color, this.sel.finish);
      if (models.length === 1) this.sel.model = models[0].key;
    }

    this.renderPanel();
  }

  reset() {
    this.sel = { cat: null, color: null, finish: null, model: null };
    this.syncTiles();
    this.panel.innerHTML = '';
    this.panel.hidden = true;
  }

  syncTiles() {
    this.querySelectorAll('[data-hnp-tile]').forEach((tile) => {
      const on = this.sel.cat && tile.dataset.hnpTile === this.sel.cat.key;
      tile.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  motionOk() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  announce(msg) {
    if (this.status) this.status.textContent = msg;
  }

  /* -------------------------------------------------------------- render */

  renderPanel() {
    const cat = this.sel.cat;
    if (!cat) return;

    const colors = this.colorsFor(cat);
    const finishes = this.sel.color ? this.finishesFor(this.sel.color) : [];
    const noFinishAxis = finishes.length === 1 && finishes[0].key === '';
    const showFinish = !this.sel.color || !noFinishAxis;

    const models =
      this.sel.color && this.sel.finish !== null ? this.modelsFor(this.sel.color, this.sel.finish) : [];
    const showModel = models.length !== 1;
    const variant = this.currentVariant();

    let n = 0;
    const steps = [];

    steps.push(this.step(++n, 'Colour', colors.map((c) => this.swatch(c)), true));

    if (showFinish) {
      steps.push(
        this.step(
          ++n,
          'Finish',
          finishes.map((f) => this.chip('finish', f.key, f.label, this.sel.finish === f.key)),
          Boolean(this.sel.color),
          'Choose a colour first'
        )
      );
    }

    if (showModel) {
      steps.push(
        this.step(
          ++n,
          'Nose model',
          models.map((m) => this.chip('model', m.key, m.label, this.sel.model === m.key)),
          Boolean(this.sel.color) && this.sel.finish !== null,
          showFinish ? 'Choose a finish first' : 'Choose a colour first'
        )
      );
    }

    this.panel.innerHTML = `
      <div class="hnp-panel" data-theme="${cat.key}">
        <div class="hnp-panel__head">
          <p class="hnp-eyebrow">${cat.title}</p>
          <button type="button" class="hnp-link" data-hnp-reset>Close</button>
        </div>
        <div class="hnp-steps">${steps.join('')}</div>
        ${this.summary(variant)}
      </div>`;

    if (variant) {
      this.announce(
        `${variant.mn}, ${variant.cn}${variant.fn ? `, ${variant.fn}` : ''}, ${variant.pf}. Ready to add to cart.`
      );
    }
  }

  step(num, label, chips, enabled, hint = '') {
    return `
      <div class="hnp-step"${enabled ? '' : ' data-locked="true"'}>
        <p class="hnp-step__label"><span class="hnp-step__num">${num}</span>${label}</p>
        ${enabled ? `<div class="hnp-chips">${chips.join('')}</div>` : `<p class="hnp-step__hint">${hint}</p>`}
      </div>`;
  }

  /** Swatch built from the real variant photo, falling back to a colour dot. */
  swatch(c) {
    const active = this.sel.color === c.h;
    // Phosphorescent and UV colours get a bloom, which is the one place this
    // section is allowed to be loud.
    // Name-based detection catches "Glow Green" and "Fluorescent Orange", but
    // the star colours are just "Green" / "Orange" / "Purple", so a category
    // can also declare that everything in it glows.
    const catGlow = this.sel.cat && this.sel.cat.glow;
    const glow = catGlow || /glow|fluorescent/.test(c.h) ? ' data-glow="true"' : '';
    const media = c.img
      ? `<img class="hnp-swatch__img" src="${c.img}" alt="" width="56" height="56" loading="lazy"${glow}>`
      : `<span class="hnp-swatch__dot" style="--sw:var(--sw-${c.h}, #6b6b6b)"${glow}></span>`;
    return `
      <button type="button" class="hnp-swatch" data-hnp-chip data-step="color" data-value="${c.h}" aria-pressed="${active}"${glow}>
        ${media}<span class="hnp-swatch__name">${c.label}</span>
      </button>`;
  }

  chip(step, value, label, active) {
    return `
      <button type="button" class="hnp-chip" data-hnp-chip data-step="${step}" data-value="${value}" aria-pressed="${active}">
        <span>${label}</span>
      </button>`;
  }

  summary(variant) {
    if (!variant) {
      return `<div class="hnp-summary" data-empty="true"><p>Work through the steps to add a nose to your cart.</p></div>`;
    }
    return `
      <div class="hnp-summary">
        ${
          variant.img
            ? `<img class="hnp-summary__img" src="${variant.img}" alt="${variant.mn}, ${variant.cn}" width="96" height="96" loading="lazy">`
            : ''
        }
        <div class="hnp-summary__text">
          <p class="hnp-summary__title">${variant.mn}</p>
          <p class="hnp-summary__meta">${variant.cn}${variant.fn ? ` &middot; ${variant.fn}` : ''}</p>
          <p class="hnp-summary__price">${variant.pf}</p>
        </div>
        <button type="button" class="hnp-add" data-hnp-submit data-variant="${variant.id}"${variant.a ? '' : ' disabled'}>
          <span data-hnp-add-text>${variant.a ? 'Add to cart' : 'Sold out'}</span>
        </button>
      </div>
      <p class="hnp-error" data-hnp-error hidden></p>`;
  }

  /* ---------------------------------------------------------------- cart */

  get cartUrl() {
    if (window.routes && window.routes.cart_url) return window.routes.cart_url;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/$/, '')}/cart`;
  }

  get addUrl() {
    if (window.routes && window.routes.cart_add_url) return window.routes.cart_add_url;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/$/, '')}/cart/add.js`;
  }

  async addToCart(button) {
    const label = button.querySelector('[data-hnp-add-text]');
    const errorEl = this.querySelector('[data-hnp-error]');
    const original = label.textContent;
    const mode = this.dataset.afterAdd || 'redirect';

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    label.textContent = 'Adding\u2026';
    if (errorEl) errorEl.hidden = true;

    const body = { items: [{ id: Number(button.dataset.variant), quantity: 1 }] };

    // Only ask for rendered sections when we actually intend to paint them.
    // Requesting them for a redirect is wasted work.
    const target =
      mode === 'drawer'
        ? document.querySelector('cart-drawer') || document.querySelector('cart-notification')
        : null;

    if (target && typeof target.getSectionsToRender === 'function') {
      body.sections = target.getSectionsToRender().map((s) => s.id).join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      const res = await fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      });
      const json = await res.json();

      // Shopify reports a real failure with a status field.
      if (json.status) {
        throw new Error(json.description || json.message || 'That variant could not be added.');
      }

      if (window.publish && window.PUB_SUB_EVENTS) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'halloween-nose-picker' });
        } catch (e) {
          /* non-fatal */
        }
      }

      if (mode === 'redirect') {
        label.textContent = 'Added';
        window.location.href = this.cartUrl;
        return;
      }

      if (mode === 'drawer') {
        let painted = false;
        if (target && typeof target.renderContents === 'function') {
          try {
            if (target.tagName === 'CART-DRAWER') target.classList.remove('is-empty');
            target.renderContents(json);
            painted = true;
          } catch (e) {
            // The drawer exists but could not render. Rather than leaving the
            // shopper with a silently added item, fall through to the cart page.
            console.warn('[halloween-nose-picker] cart drawer render failed, going to cart', e);
          }
        }
        if (!painted) {
          label.textContent = 'Added';
          window.location.href = this.cartUrl;
          return;
        }
      }

      if (mode === 'stay') await this.refreshBubble();

      label.textContent = 'Added';
      this.announce('Added to cart.');
      setTimeout(() => {
        label.textContent = original;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1800);
    } catch (err) {
      label.textContent = original;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
      this.announce(err.message);
      console.error('[halloween-nose-picker] add to cart failed', err);
    }
  }

  async refreshBubble() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root.replace(/\/$/, '')}/?sections=cart-icon-bubble`);
      const json = await res.json();
      const mount = document.getElementById('cart-icon-bubble');
      const html = json['cart-icon-bubble'];
      if (!html || !mount) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (parsed) mount.innerHTML = parsed.innerHTML;
    } catch (err) {
      /* cosmetic only */
    }
  }
}

if (!customElements.get('halloween-nose-picker')) {
  customElements.define('halloween-nose-picker', HalloweenNosePicker);
}
